import React, { useEffect, useId, useState } from 'react';

export interface ReadinessCheck {
    title: string;
    detail: string;
    done: boolean;
    action?: string;
}

// Un'icona per voce: nella versione ridotta le voci diventano pastiglie.
const ICONS: Record<string, string> = {
    'Icona': 'bi-image',
    'Descrizione breve': 'bi-text-paragraph',
    'Scheda nel portale': 'bi-window',
    'Pagina pubblica': 'bi-globe2',
    'Guida utente': 'bi-book',
    'Nessun problema aperto': 'bi-shield-check',
};

const STORAGE_KEY = 'admin.toolReadiness.collapsed';
const readCollapsed = () => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } };

/** Anello di avanzamento: frazione completata, numero al centro. */
const Ring: React.FC<{ done: number; total: number; size: number }> = ({ done, total, size }) => {
    const stroke = size > 40 ? 5 : 4;
    const radius = (size - stroke) / 2;
    const length = 2 * Math.PI * radius;
    return (
        <span className="td-ring" style={{ width: size, height: size }} role="img" aria-label={`${done} voci su ${total} completate`}>
            <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} aria-hidden="true">
                <circle cx={size / 2} cy={size / 2} r={radius} className="td-ring__track" strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={radius} className="td-ring__value" strokeWidth={stroke}
                    strokeDasharray={length} strokeDashoffset={length * (1 - done / total)} />
            </svg>
            <span className="td-ring__label" aria-hidden="true">{done}<small>/{total}</small></span>
        </span>
    );
};

/**
 * "Pronto per gli utenti": lista di controllo del tool. Si riduce a una riga con anello,
 * pastiglie e prossimo passo; la scelta resta ricordata in questo browser. Quando è tutto
 * pronto parte ridotta, se l'utente non ha scelto diversamente.
 */
const ReadinessCard: React.FC<{ checks: ReadinessCheck[]; onAct: (check: ReadinessCheck) => void }> = ({ checks, onAct }) => {
    const done = checks.filter(c => c.done).length;
    const complete = done === checks.length;
    const [collapsed, setCollapsed] = useState(() => {
        const saved = readCollapsed();
        return saved === null ? complete : saved === '1';
    });
    const bodyId = useId();
    const next = checks.find(c => !c.done && c.action);

    // Senza una scelta esplicita, la scheda si riduce da sola quando tutto è pronto.
    useEffect(() => { if (readCollapsed() === null) setCollapsed(complete); }, [complete]);

    const toggle = () => setCollapsed(value => {
        try { localStorage.setItem(STORAGE_KEY, value ? '0' : '1'); } catch { /* storage non disponibile */ }
        return !value;
    });

    return (
        <section className={`td-card td-ready${collapsed ? ' is-collapsed' : ''}${complete ? ' is-complete' : ''}`} aria-labelledby="td-ready-title">
            <div className="td-ready__bar-row">
                <Ring done={done} total={checks.length} size={collapsed ? 38 : 46} />
                <div className="td-ready__heading">
                    <h2 id="td-ready-title" className="td-card__title">{complete ? 'Tutto pronto per gli utenti' : 'Pronto per gli utenti'}</h2>
                    {!collapsed && <p className="td-card__lead">Quello che serve perché il tool si presenti bene nel portale e sul sito.</p>}
                </div>

                {collapsed && (
                    <ul className="td-ready__pills" aria-label="Voci della lista">
                        {checks.map(check => (
                            <li key={check.title}>
                                <button type="button" className={`td-pill${check.done ? ' is-done' : ''}`} onClick={() => onAct(check)}
                                    aria-label={`${check.title}: ${check.done ? 'fatto' : 'da completare'}. ${check.detail}`}
                                    title={`${check.title} · ${check.detail}`}>
                                    <i className={`bi ${ICONS[check.title] ?? 'bi-circle'}`} aria-hidden="true" />
                                    {check.done && <span className="td-pill__tick" aria-hidden="true"><i className="bi bi-check" /></span>}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {collapsed && next && (
                    <button type="button" className="td-ready__next" onClick={() => onAct(next)}>
                        <span className="td-ready__next-label">Prossimo passo</span>
                        <span className="td-ready__next-text">{next.title}: {next.action?.toLowerCase()}</span>
                        <i className="bi bi-arrow-right" aria-hidden="true" />
                    </button>
                )}

                <button type="button" className="td-ready__toggle" onClick={toggle} aria-expanded={!collapsed} aria-controls={bodyId}
                    aria-label={collapsed ? 'Mostra la lista completa' : 'Riduci a una riga'} title={collapsed ? 'Mostra la lista completa' : 'Riduci a una riga'}>
                    <i className="bi bi-chevron-down" aria-hidden="true" />
                </button>
            </div>

            <div id={bodyId} className="td-ready__body" inert={collapsed}>
                <div className="td-ready__inner">
                    <ul className="td-ready__list">
                        {checks.map(check => (
                            <li key={check.title} className="td-ready__item">
                                <span className={`td-ready__mark${check.done ? ' is-done' : ''}`} aria-hidden="true">
                                    {check.done ? <i className="bi bi-check-lg" /> : <i className={`bi ${ICONS[check.title] ?? 'bi-circle'}`} />}
                                </span>
                                <span className="td-ready__text">
                                    <span className="td-ready__title">
                                        {check.title}<span className="visually-hidden">{check.done ? ': fatto' : ': da completare'}</span>
                                    </span>
                                    <span className="td-ready__detail">{check.detail}</span>
                                </span>
                                {check.action && (
                                    <button type="button" className="td-link" onClick={() => onAct(check)}>{check.action}</button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default ReadinessCard;
